import React, { FC, useCallback, useEffect, useState } from 'react';
import { checkMarxSeverityProps, productSecurityProps } from './types';
import { BaseContainer } from '../baseContainer';
import { LinearProgress, makeStyles, Tooltip } from '@material-ui/core';
import { EntityDisplayName, useEntity } from '@backstage/plugin-catalog-react';
import { EmptyState } from '@backstage/core-components';
const useStyles = makeStyles({
  body: {
    background: '#fff',
    border: '1px solid #dce1e8',
    boxShadow: '0 1px 3px rgba(5, 22, 77, 0.08)',
    color: '#172d62',
    marginTop: '12px',
    padding: '16px',
    width: '100%',
  },
  tile: {
    border: '1px solid #dce1e8',
    boxSizing: 'border-box',
    minHeight: '84px',
    padding: '12px 10px',
  },
  countNumber: {
    fontWeight: 700,
    fontSize: '32px',
    lineHeight: 1,
    margin: 0,
  },
  numberLabel: {
    fontWeight: 400,
    fontSize: '12px',
    margin: '12px 0 0',
  },
  tileBody: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: '8px',
    marginTop: '12px',
    width: '100%',
  },
  productName: {
    display: 'inline',
    fontWeight: 700,
    marginLeft: '4px',
  },
  critical: {
    color: '#be2a32',
  },
  high: {
    color: '#f0625f',
  },
  medium: {
    color: '#f7ad4a',
  },
  low: {
    color: '#c0c0c0',
  },
  info: {
    color: '#05164d',
  },
  severityBar: {
    display: 'flex',
    height: '14px',
    borderRadius: '0',
    overflow: 'hidden',
    background: '#dce1e8',
    marginTop: '12px',
  },
  severitySegment: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '32px',
    color: '#fff',
    fontWeight: 700,
    fontSize: '0',
    borderRight: '1px solid #fff',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    margin: '16px 0 0',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
  },
  legendSwatch: {
    width: '14px',
    height: '14px',
  },
});

const severitySegments = [
  { key: 'critical', label: 'Critical', color: '#be2a32' },
  { key: 'high', label: 'High', color: '#f0625f' },
  { key: 'medium', label: 'Medium', color: '#f7ad4a' },
  { key: 'low', label: 'Low', color: '#c0c0c0' },
  { key: 'info', label: 'Info', color: '#a6a6a6' },
] as const;

enum reportType {
  Critcal = 'Critical',
  High = 'High',
  Medium = 'Medium',
  Low = 'Low',
  Info = 'Info',
}

const CheckmarxComponent: FC<{ data: productSecurityProps }> = ({ data }) => {
  const styles = useStyles();
  const [checkmarxProjects, setCheckmarxProjects] =
    useState<checkMarxSeverityProps>();
  const [isLoading, setIsLoading] = useState(true);
  const { entity } = useEntity();

  const fetchComponentList = useCallback(async () => {
    if (!data.catalogEntities) {
      return undefined;
    }
    const fetchData = (window as any).fetchData;
    const urls = (window as any).urls;
    const id = entity?.metadata?.name?.trim().toLowerCase();
    if (!id) {
      return undefined;
    }
    const getApplicationByTag = await fetchData(
      urls.checkmarxgetApplicationByTag,
    );
    console.log(getApplicationByTag);
    if (!getApplicationByTag) {
      return undefined;
    }

    return getApplicationByTag?.find(
      (app: any) =>
        app.tags != null &&
        Object.keys(app.tags).some(tag => tag.trim().toLowerCase() === id?.trim().toLowerCase()),
    );
  }, [data.catalogEntities, entity?.metadata?.name]);

  useEffect(() => {
    if (!data) {
      setIsLoading(false);
      return;
    }

    if (!data.catalogEntities) {
      return;
    }

    setIsLoading(true);
    fetchComponentList()
      .then(res => {
        if (!res) {
          return;
        }

        const response: checkMarxSeverityProps = {
          severityCounters: res?.totalCounters?.severityCounters ?? [],
          projectNumber: res.projectNumber,
          applicationId: res.applicationId,
        };
        setCheckmarxProjects(response);
      })
      .catch(() => {
        return setCheckmarxProjects(undefined);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [data, fetchComponentList]);
  const productName = (
    <a
      className={styles.productName}
      href={`https://eu-2.ast.checkmarx.net/applications/${checkmarxProjects?.applicationId}/overview`}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: '#172d62' }}
    >
      <EntityDisplayName entityRef={data?.appName || data?.product.name} />
    </a>
  );

  const getSeverityCount = (severity: reportType) =>
    checkmarxProjects?.severityCounters.find(
      counter => counter.severity.toLowerCase() === severity.toLowerCase(),
    )?.counter ?? 0;
  const critical = getSeverityCount(reportType.Critcal);
  const high = getSeverityCount(reportType.High);
  const medium = getSeverityCount(reportType.Medium);
  const low = getSeverityCount(reportType.Low);
  const info = getSeverityCount(reportType.Info);

  const total = critical + high + medium + low + info;
  const severityCounts = { critical, high, medium, low, info };
  const projectCount = checkmarxProjects?.projectNumber ?? 0;
  return (
    <div className={styles.body}>
      <BaseContainer
        title="Application Security Posture"
        subTitle={<label>Checkmarx One Application{productName}</label>}
      />
      {!isLoading && Object.keys(data).length > 0 && projectCount > 0 && (
        <div className={styles.tileBody}>
          <div className={styles.tile}>
            <p className={`${styles.countNumber}  ${styles.info}`}>
              {projectCount}
            </p>
            <p className={styles.numberLabel}>Project(s)</p>
          </div>
          <div className={styles.tile}>
            <p className={`${styles.countNumber} ${styles.critical}`}>
              {critical}
            </p>
            <p className={styles.numberLabel}>Critical risk</p>
          </div>
          <div className={styles.tile}>
            <p className={`${styles.countNumber} ${styles.high}`}>{high}</p>
            <p className={styles.numberLabel}>High risk</p>
          </div>
          <div className={styles.tile}>
            <p className={`${styles.countNumber} ${styles.info}`}>{total}</p>
            <p className={styles.numberLabel}>Vulnerabilities</p>
          </div>
        </div>
      )}
      {!isLoading && Object.keys(data).length > 0 && projectCount > 0 && (
        <>
          <div className={styles.legend}>
            {severitySegments.map(s => (
              <span key={s.key} className={styles.legendItem}>
                <span
                  className={styles.legendSwatch}
                  style={{ background: s.color }}
                />
                {s.label}
              </span>
            ))}
          </div>
          <div className={styles.severityBar}>
            {severitySegments
              .filter(s => severityCounts[s.key] > 0)
              .map(s => (
                <Tooltip
                  key={s.key}
                  title={`${s.label}: ${severityCounts[s.key]}`}
                  arrow
                >
                  <div
                    className={styles.severitySegment}
                    style={{ flex: severityCounts[s.key], background: s.color }}
                  >
                    {severityCounts[s.key]}
                  </div>
                </Tooltip>
              ))}
          </div>
        </>
      )}
      {isLoading && <LinearProgress />}
      {!isLoading && (projectCount === 0 || Object.keys(data).length < 1) && (
        <EmptyState missing="data" title="No data from Checkmarx." />
      )}
    </div>
  );
};

export { CheckmarxComponent };
